import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { memberInfo, memberUpdate } from "../../api/member";
import { fileApiService } from "../../api/apiService";
import { BoxButtonTitle, InputTitle } from "../../style/common";
import { flexUnsetStart } from "../../style/mixins";
import TitleAndInput from "../common/TitleAndInput";
import AddBtns from "./AddBtns";
import { useAlertStore } from "../../store/alertStore";
import { preventEnterKey } from "../../utils/keyEvents";
import { formatToE164, formatToNational } from "../../utils/phoneFormatChange";
import { stringToJson } from "../../utils/stringToJson";
import { dateHyphen } from "../../utils/dateFormChange";
import { useNavigatePage } from "../../hooks/useNavigatePage";


export default function Add() {
  const { idx } = useParams<{ idx: string }>();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<MemberSignupInput>();
  const isSignup = idx === "add";
  const targetIdx = !isSignup ? idx : undefined;
  const setAlert = useAlertStore((state) => state.setAlert);
  const { onMovementPage } = useNavigatePage();
  const [memberImg, setMemberImg] = useState<string | null>(null);
  const [memberImgfile, setMemberImgfile] = useState<string | null>(null);

  const onSingUpSubmit: SubmitHandler<MemberSignupInput> = async (data) => {
    const {
      name,
      phone,
      com,
      sub,
      team,
      com_email,
      sub_email,
    } = data;

    const memberPublicExtras = JSON.stringify({ com, sub, team, com_email, sub_email });

    const commonUpData = {
      member_name: name,
      member_phone: formatToE164(phone, "KR"),
      member_public_extras: memberPublicExtras,
    };

    try {
      if (targetIdx) {
        const memberPutData: MemberUpdateProps = {
          idx: targetIdx,
          ...commonUpData,
        };

        const response = await memberUpdate(memberPutData);
        if (response.statusCode === 200) {
          setAlert("회원 정보가 수정되었습니다.", "green");
          onMovementPage(`/user/detail/${targetIdx}`);
        }
      }
    } catch (error: any) {
      setAlert("요청을 처리하는 중 오류가 발생했습니다.", "red");
    }
  };

  const onError = () => {
    if (errors.id) {
      setAlert(errors.id.message || "", "red");
    } else if (errors.pw) {
      setAlert(errors.pw.message || "", "red");
    } else if (errors.phone) {
      setAlert(errors.phone.message || "", "red");
    }
  };

  useEffect(() => {
    if (targetIdx) {
      const fetchUserInfo = async () => {
        try {
          const params = { idx: targetIdx };
          const response = await memberInfo(params);
          const data = response.data;
          const memberPublicExtra = data.member_public_extras ? stringToJson(data.member_public_extras) : {};
          
          if (data.files && data.files[0]?.file_url) {
            const getFilesResponse = await fileApiService.get<Blob>(data.files[0].file_url, {
              responseType: "arraybuffer" as ResponseType,
            });
            const imgUrlBlob = new Blob([getFilesResponse], { type: "image/jpeg" });
            const objectUrl = URL.createObjectURL(imgUrlBlob);
            setMemberImgfile(objectUrl);
            setMemberImg(data.files[0]?.file_url || null);
          }

          reset({
            id: data.member_id || "",
            pw: "",
            img: "",
            name: data.member_name || "",
            phone: data.member_phone ? formatToNational(data.member_phone, "KR") : "",
            com: memberPublicExtra.com || "",
            sub: memberPublicExtra.sub || "",
            team: memberPublicExtra.team || "",
            com_email: memberPublicExtra.com_email || "",
            sub_email: memberPublicExtra.sub_email || "",
            date_active: data.date_active ? dateHyphen(data.date_active, "YYYY-MM-DD") : "-",
            login_ip: data.member_login_ip || "접속 IP 없음",
            date_login: data.date_login ? dateHyphen(data.date_login, "YYYY-MM-DD HH:mm") : "최근 접속 기록 없음",
            date_delete: data.date_delete ? dateHyphen(data.date_delete, "YYYY-MM-DD HH:mm") : "-",
            date_block: data.date_block ? dateHyphen(data.date_block, "YYYY-MM-DD HH:mm") : "-",
          });
        }
        catch (error: any) {
        }
      };

      fetchUserInfo();
    } else {
      reset({
        id: "",
        pw: "초기화시 a123456789!",
        img: "",
        name: "",
        phone: "",
        com: "",
        sub: "",
        team: "",
        com_email: "",
        sub_email: "",
        date_active: "",
        login_ip: "",
        date_login: "",
        date_delete: "",
      });
    }
  }, [idx, reset, isSignup, targetIdx]);

  return (
    <form onSubmit={handleSubmit(onSingUpSubmit, onError)} onKeyDown={preventEnterKey}>
      <BoxButtonTitleCustom>
        회원{isSignup ? "추가" : "수정"}
        <AddBtns />
      </BoxButtonTitleCustom>

      <InputGridBox>
        <TitleAndInput
          title="아이디(이메일)"
          type="text"
          name="id"
          placeholder="이메일 형식으로 입력해주세요."
          register={register("id", {
            required: "아이디는 필수 입력 항목입니다.",
          })}
          disabled={true}
        />
        <TitleAndInput
          title="비밀번호"
          type="password"
          name="pw"
          placeholder={isSignup ? "초기화시 a123456789!" : "변경시만 입력"}
          register={register("pw", {
            required: isSignup ? "비밀번호는 필수 입력 항목입니다." : false,
            validate: (value) => {
              if (!value) return true;
              return /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{4,}$/.test(value) || 
                "영문, 숫자를 포함한 4자 이상 입력해주세요.";
            },
          })}
          disabled={true}
        />
        
        <UserImgBox>
          <InputTitle>{!isSignup && "회원사진"}</InputTitle>
          <ImgLayout>{memberImg ? <MemberImg src={memberImgfile || memberImg} /> : "등록한 이미지 없음"}</ImgLayout>
        </UserImgBox>

        <TitleAndInput
          title="이름"
          type="text"
          name="name"
          placeholder="회원 이름"
          register={register("name")}
        />
        <TitleAndInput
          title="휴대폰 번호"
          type="tel"
          name="phone"
          placeholder="- 포함하여 입력"
          register={register("phone", {
            required: "휴대폰은 필수 입력 항목입니다.",
            pattern: {
              value: /^010-\d{4}-\d{4}$/,
              message: "휴대폰만 가능하며, - 포함하여 입력해주세요."
            }
          })}
        />

        <TitleAndInput
          title="회사명"
          type="text"
          name="com"
          placeholder="회원 회사명"
          register={register("com")}
        />
        <TitleAndInput
          title="현장명"
          type="text"
          name="sub"
          placeholder="회원 현장명"
          register={register("sub")}
        />
        <TitleAndInput
          title="팀명"
          type="text"
          name="team"
          placeholder="팀명"
          register={register("team")}
        />

        <TitleAndInput
          title="본사 메일"
          type="email"
          name="com_email"
          placeholder="본사 메일"
          register={register("com_email")}
        />
        <TitleAndInput
          title="원청사 메일"
          type="email"
          name="sub_email"
          placeholder="원청사 메일"
          register={register("sub_email")}
        />
        {!isSignup && (
          <TitleAndInput
            title="회원가입일"
            type="none"
            name="date_active"
            register={register("date_active")}
          />
        )}
      </InputGridBox>

      {!isSignup && (
        <InputGridBox>
          <TitleAndInput
            title="IP"
            type="none"
            name="login_ip"
            register={register("login_ip")}
          />
          <TitleAndInput
            title="최근접속일"
            type="none"
            name="date_login"
            register={register("date_login")}
          />
          <div></div>

          <TitleAndInput
            title="탈퇴일자"
            type="none"
            name="date_delete"
            register={register("date_delete")}
          />
          <TitleAndInput
            title="차단일자"
            type="none"
            name="date_block"
            register={register("date_block")}
          />
        </InputGridBox>
      )}

      {/* <CommonFooter>
        <div></div>
        <AddBtns />
      </CommonFooter> */}
    </form>
  );
}

const BoxButtonTitleCustom = styled(BoxButtonTitle)`
  margin-bottom: 2.5rem;
`;

const InputGridBox = styled.div`
  width: 100%;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid #DEE2E6;
  margin-bottom: 2.5rem;
  display: grid;
  grid-template-columns: repeat(3, 326px);
  gap: 0.75rem 5%;
`;

const UserImgBox = styled.div`
  ${flexUnsetStart};
  grid-row: span 3;

  h6 {
    margin-top: 0.5rem;
  }
`;

const MemberImg = styled.img`
  width: auto;
  height: 120px;
`;  

const ImgLayout = styled.div`
  padding-left: 1rem;
`;
